import type { DoohReportEvent } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

/** Converts a share over the campaign target reach (0-100) into a fraction. */
const PERCENT_TO_RATIO = 100;

/**
 * OTA figures quoted in an event's `otaReasons`, e.g. "x1.6", "x3,5" or "×2" — only when not
 * glued to a preceding letter or digit, so the "x2" inside "max2" is not one — together with the
 * "by" that introduces them ("…on your target by x666.7 because…").
 */
const OTA_FIGURE_PATTERN = /(?:\s+by)?\s*(?<![\p{L}\p{N}])[x×]\d+(?:[.,]\d+)?/gu;

/**
 * The event that added the most target reach to the campaign, or null when no event added any.
 * The event lift (the event's OTA) is not used: it measures how concentrated the target was at the
 * event's screens, so a small event can top it without weighing anything in the plan.
 */
export function findHighestImpactEvent(events: DoohReportEvent[]): DoohReportEvent | null {
  return events.reduce<DoohReportEvent | null>(
    (highest, event) => ((event.reachAddedPoints ?? 0) > (highest?.reachAddedPoints ?? 0) ? event : highest),
    null
  );
}

/**
 * An event's `otaReasons` without the OTA figures it quotes: the event lift is shown on its own
 * column, capped, and an exact "x666.7" in a sentence would undo that. Null when nothing is left.
 */
export function stripOtaFigures(otaReasons: string | null): string | null {
  const text = otaReasons
    ?.replace(OTA_FIGURE_PATTERN, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return text || null;
}

/**
 * Share (0-100) of impacts that reached the target audience during the event: the
 * untargeted plan's share scaled by the event's OTA, capped at 100. Null when either is unknown.
 */
export function getOnTargetWithEventPct(baselineOnTargetPct: number | null, eventOta: number | null): number | null {
  if (baselineOnTargetPct === null || eventOta === null) {
    return null;
  }

  return Math.min(100, baselineOnTargetPct * eventOta);
}

/**
 * Campaign OTA the plan would have without the event: the event's share of the campaign target
 * reach (`onTargetUpliftPct`) taken out of it, with the same untargeted baseline. The event's
 * effect on the plan reads "campaign OTA {this} → {campaignOta}", bounded by how much of the plan
 * the event weighs. Null when either figure is unknown.
 */
export function getCampaignOtaWithoutEvent(
  campaignOta: number | null,
  onTargetUpliftPct: number | null
): number | null {
  if (campaignOta === null || onTargetUpliftPct === null) {
    return null;
  }

  return campaignOta * Math.max(0, 1 - onTargetUpliftPct / PERCENT_TO_RATIO);
}
